import React, { useEffect, useMemo, useRef } from 'react';
import { Label } from '@/components/atoms/texts/label';
import { useTranslation } from 'react-i18next';
import MessageBubble from './MessageBubble';
import HistoryBubble from './HistoryBubble';

const getDayKey = (dateString) => {
    const date = new Date(dateString);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
};

const formatDayLabel = (dateString, t) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);

    if (getDayKey(dateString) === getDayKey(today.toISOString())) {
        return t('management.onboarding.chat.today');
    }
    if (getDayKey(dateString) === `${yesterday.getFullYear()}-${yesterday.getMonth()}-${yesterday.getDate()}`) {
        return t('management.onboarding.chat.yesterday');
    }

    return date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
};

const ChatArea = ({
    conversationKey,
    scrollToBottomSignal,
    messages,
    profileEvents,
    loading,
    eventsLoading,
    error,
    hasNextPage,
    onLoadOlder,
    onEdit,
    onDelete,
    editingMessageUuid,
    onUnreadMessageClick,
}) => {
    const [t, i18n] = useTranslation('global');
    const scrollRef = useRef(null);
    const didInitialScrollRef = useRef(false);

    const sortedMessages = useMemo(() => {
        return [...messages].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }, [messages]);

    const sortedProfileEvents = useMemo(() => {
        return [...(profileEvents || [])].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }, [profileEvents]);

    const timelineEntries = useMemo(() => {
        const messageEntries = sortedMessages.map((message) => ({
            type: 'message',
            id: message.uuid,
            timestamp: message.createdAt,
            payload: message,
        }));

        const eventEntries = sortedProfileEvents.map((event, index) => ({
            type: 'event',
            id: `${event.timestamp}-${event.eventCode}-${index}`,
            timestamp: event.timestamp,
            payload: event,
        }));

        return [...messageEntries, ...eventEntries].sort((a, b) => {
            const aTime = new Date(a.timestamp).getTime();
            const bTime = new Date(b.timestamp).getTime();

            if (aTime !== bTime) {
                return aTime - bTime;
            }

            if (a.type === b.type) {
                return 0;
            }

            return a.type === 'event' ? -1 : 1;
        });
    }, [sortedMessages, sortedProfileEvents]);

    const firstUnreadMessageUuid = useMemo(() => {
        const firstUnreadMessage = sortedMessages.find((message) => !message.adminSender && !message.adminReadAt && !message.deletedAt);
        return firstUnreadMessage?.uuid || null;
    }, [sortedMessages]);

    const hasTimelineEntries = timelineEntries.length > 0;
    const showInitialLoader = (loading || eventsLoading) && !error && !hasTimelineEntries;
    const showError = Boolean(error) && !hasTimelineEntries;
    const unreadCount = sortedMessages.filter((message) => !message.adminSender && !message.adminReadAt && !message.deletedAt).length;

    useEffect(() => {
        didInitialScrollRef.current = false;
    }, [conversationKey]);

    useEffect(() => {
        if (didInitialScrollRef.current || !hasTimelineEntries) return;

        const node = scrollRef.current;
        if (!node) return;

        requestAnimationFrame(() => {
            node.scrollTop = node.scrollHeight;
            didInitialScrollRef.current = true;
        });
    }, [hasTimelineEntries]);

    useEffect(() => {
        if (!hasTimelineEntries) return;

        const node = scrollRef.current;
        if (!node) return;

        requestAnimationFrame(() => {
            node.scrollTop = node.scrollHeight;
        });
    }, [scrollToBottomSignal, hasTimelineEntries]);

    const handleScroll = () => {
        const node = scrollRef.current;
        if (!node || !hasNextPage || loading) return;
        if (node.scrollTop <= 40) {
            onLoadOlder();
        }
    };

    return (
        <div
            ref={scrollRef}
            className="no-scrollbar flex-1 min-h-0 overflow-y-auto bg-[hsl(var(--medium))] px-3.5 py-3"
            onScroll={handleScroll}
            role="log"
            aria-live="polite"
        >
            <div className="flex min-h-full flex-col justify-end gap-1.5">
                {showInitialLoader && <span className="loader" />}
                {showError && <Label color="error" message="error.fetchUnsuccessfull" />}

                {!loading && !eventsLoading && !error && !hasTimelineEntries && (
                    <div className="h-full flex items-center justify-center">
                        <Label message="management.onboarding.empty.noMessages" />
                    </div>
                )}

                {!error && hasTimelineEntries && timelineEntries.map((entry, index) => {
                    const previous = timelineEntries[index - 1];
                    const hasDaySeparator = !previous || getDayKey(previous.timestamp) !== getDayKey(entry.timestamp);
                    const message = entry.type === 'message' ? entry.payload : null;
                    const event = entry.type === 'event' ? entry.payload : null;
                    const isOwn = message ? Boolean(message.adminSender) : false;
                    const isUnread = message ? !isOwn && !message.adminReadAt : false;
                    const isEditingCurrentMessage = message ? editingMessageUuid === message.uuid : false;
                    const showUnreadSeparator = message && firstUnreadMessageUuid && message.uuid === firstUnreadMessageUuid;

                    return (
                        <React.Fragment key={entry.id}>
                            {hasDaySeparator && (
                                <div className="my-1 flex justify-center">
                                    <Label className="rounded-full bg-primary/5 px-2.5 py-0.5 text-[10px] font-medium text-primary-hover">
                                        {formatDayLabel(entry.timestamp, t)}
                                    </Label>
                                </div>
                            )}

                            {showUnreadSeparator && (
                                <div className="my-1.5 flex items-center gap-2">
                                    <div className="h-px flex-1 bg-secondary/40" />
                                    <Label className="whitespace-nowrap rounded-full border border-secondary/40 bg-secondary/15 px-2.5 py-0.5 text-[10px] font-semibold text-cyan-700">
                                        {t('management.onboarding.chat.newMessages', { count: unreadCount })}
                                    </Label>
                                    <div className="h-px flex-1 bg-secondary/40" />
                                </div>
                            )}

                            {entry.type === 'event' && event && (
                                <HistoryBubble event={event} locale={i18n.language} />
                            )}

                            {entry.type === 'message' && message && !isEditingCurrentMessage && (
                                <MessageBubble
                                    message={message}
                                    isOwn={isOwn}
                                    isUnread={isUnread}
                                    onEdit={() => onEdit(message)}
                                    onDelete={() => onDelete(message)}
                                    onUnreadMessageClick={() => onUnreadMessageClick?.(message)}
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default ChatArea;
